import CookieRepository from 'frontcore/domain/cookies/model/CookieRepository'
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  BasicQueryStringUtils,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  LocationLike,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth'
import {
  AuthorizationSSOAPIClient,
  AuthorizationSSOModel,
} from 'frontcore/domain/authorization/model/AuthorizationService.d'
import { AuthorizationSSOConfiguration } from 'frontcore/domain/authorization/model/types'
import { JWT } from 'frontcore/utils/jwt/jwt'
import { SameSiteOption } from 'frontcore/domain/cookies/types'

// Workaround to avoid importing @openid/appauth type and breaking ts compilation
interface TokenRequestJson {
  grant_type: string
  code?: string
  refresh_token?: string, redirect_uri: string
  client_id: string
  extras?: Record<string, string>
}

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse (input: LocationLike) {
    return super.parse(input, false)
  }
}

export default class AuthorizationOAuthAPIClient implements AuthorizationSSOAPIClient {

  #callback: ((model: AuthorizationSSOModel) => void) | undefined
  readonly #configuration: AuthorizationServiceConfiguration
  readonly #notifier: AuthorizationNotifier
  readonly #requestHandler: RedirectRequestHandler
  readonly #scope = 'openid profile email'
  readonly #tokenHandler: BaseTokenRequestHandler = new BaseTokenRequestHandler(new FetchRequestor())
  readonly #tokenRequest: TokenRequestJson

  constructor (configuration: AuthorizationSSOConfiguration) {
    this.#configuration = new AuthorizationServiceConfiguration({
      authorization_endpoint: configuration.authorizationEndpoint,
      token_endpoint: configuration.tokenEndpoint,
      revocation_endpoint: '',
      end_session_endpoint: configuration.endSessionEndpoint,
    })
    this.#notifier = new AuthorizationNotifier()
    this.#requestHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location
    )
    this.#tokenRequest = {
      client_id: configuration.clientId,
      redirect_uri: configuration.redirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
    }
  }

  addListener = (callback: (model: AuthorizationSSOModel) => void): void => {
    this.#callback = callback
    this.#notifier.setAuthorizationListener((request, response) => {
      if (!response) { return }
      this.#tokenRequest.code = response.code
      if (request?.internal?.code_verifier) {
        this.#tokenRequest.extras = { code_verifier: request.internal.code_verifier }
      }
      this.requestToken()
    })
    this.#requestHandler.setAuthorizationNotifier(this.#notifier)
    this.#requestHandler.completeAuthorizationRequestIfPossible()
  }

  login = (): void => {
    const request = new AuthorizationRequest({
      client_id: this.#tokenRequest.client_id,
      redirect_uri: this.#tokenRequest.redirect_uri,
      scope: this.#scope,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
    })
    this.#requestHandler.performAuthorizationRequest(this.#configuration, request)
  }

  requestToken = async (): Promise<void> => {
    const request = new TokenRequest({
      client_id: this.#tokenRequest.client_id,
      redirect_uri: this.#tokenRequest.redirect_uri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: this.#tokenRequest?.code,
      extras: this.#tokenRequest?.extras,
    })
    try {
      const response = await this.#tokenHandler.performTokenRequest(this.#configuration, request)
      if (response.idToken) {
        const cookieRepo = new CookieRepository()
        const currentMillis = Date.now()
        const token = new JWT(response.idToken)
        const maxAge = Math.floor(token.exp - currentMillis / 1000)
        const idTokenCookie = {
          key: 'idToken',
          value: response.idToken,
          isSecure: true,
          maxAge,
          sameSite: SameSiteOption.STRICT,
          path: '/',
        }
        cookieRepo.set(idTokenCookie)
      }
      this.#callback?.({
        accessToken: response.accessToken,
        idToken: response.idToken,
        isAuthenticated: response.isValid(),
      })
    } catch (e) {
      this.#callback?.({ isAuthenticated: false })
    }
  }

}
